import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import { AU_STATES, Customer, CustomerPayload, ListTag, TagInput } from '../../../types/lists.types';
import { customersApi } from '../../../utils/lists-api';
import TagCombobox from './TagCombobox';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    customer: Customer | null;
    categories: ListTag[];
    onSaved: () => void;
}

const EMPTY: CustomerPayload = {
    legalName: '',
    tradingName: '',
    abnNumber: '',
    address: '',
    state: '',
    website: '',
    contactName: '',
    phone: '',
    email: '',
    facebook: '',
    linkedin: '',
};

export const CustomerFormDialog: React.FC<Props> = ({
    open,
    onOpenChange,
    customer,
    categories,
    onSaved,
}) => {
    const [form, setForm] = useState<CustomerPayload>(EMPTY);
    const [tags, setTags] = useState<TagInput>([]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!open) return;
        if (customer) {
            setForm({
                legalName: customer.legalName,
                tradingName: customer.tradingName,
                abnNumber: customer.abnNumber ?? '',
                address: customer.address ?? '',
                state: customer.state,
                website: customer.website ?? '',
                contactName: customer.contactName ?? '',
                phone: customer.phone ?? '',
                email: customer.email ?? '',
                facebook: customer.facebook ?? '',
                linkedin: customer.linkedin ?? '',
            });
            setTags((customer.categories || []).map((c) => c.id));
        } else {
            setForm(EMPTY);
            setTags([]);
        }
        setErrors({});
    }, [open, customer]);

    const set = (key: keyof CustomerPayload, value: string) =>
        setForm((f) => ({ ...f, [key]: value }));

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.legalName.trim()) next.legalName = 'Legal Name is required';
        if (!form.tradingName.trim()) next.tradingName = 'Trading Name is required';
        if (!form.state) next.state = 'State is required';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setSaving(true);
            const payload = { ...form, categories: tags };
            if (customer) {
                await customersApi.update(customer.id, payload);
                toast.success('Customer updated');
            } else {
                await customersApi.create(payload);
                toast.success('Customer created');
            }
            onSaved();
            onOpenChange(false);
        } catch (err: any) {
            // api.ts already surfaces the error toast; nothing to add here.
            console.error('Failed to save customer', err);
        } finally {
            setSaving(false);
        }
    };

    const field = (
        key: keyof CustomerPayload,
        label: string,
        opts: { required?: boolean; type?: string; placeholder?: string } = {}
    ) => (
        <div className="space-y-2">
            <Label htmlFor={`customer-${key}`}>
                {label} {opts.required && <span className="text-destructive">*</span>}
            </Label>
            <Input
                id={`customer-${key}`}
                type={opts.type || 'text'}
                placeholder={opts.placeholder}
                value={(form[key] as string) || ''}
                onChange={(e) => set(key, e.target.value)}
            />
            {errors[key] && <p className="text-xs text-destructive">{errors[key]}</p>}
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-lg font-medium">
                        {customer ? 'Edit Customer' : 'Add Customer'}
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        Customers are shared reference data. Inactive customers are kept, not deleted.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="py-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {field('legalName', 'Legal Name', { required: true })}
                            {field('tradingName', 'Trading Name', { required: true })}
                            {field('abnNumber', 'ABN Number', { placeholder: 'Australian Business Number' })}

                            <div className="space-y-2">
                                <Label htmlFor="customer-state">
                                    State <span className="text-destructive">*</span>
                                </Label>
                                <Select
                                    value={form.state || undefined}
                                    onValueChange={(v) => set('state', v)}
                                >
                                    <SelectTrigger id="customer-state" className="w-full bg-white">
                                        <SelectValue placeholder="Select a state" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {AU_STATES.map((s) => (
                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.state && <p className="text-xs text-destructive">{errors.state}</p>}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="customer-address">Address</Label>
                            <Textarea
                                id="customer-address"
                                rows={2}
                                value={form.address || ''}
                                onChange={(e) => set('address', e.target.value)}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {field('website', 'Website', { type: 'url', placeholder: 'https://' })}
                            {field('contactName', 'Contact Name')}
                            {field('phone', 'Phone', { type: 'tel' })}
                            {field('email', 'Email', { type: 'email' })}
                            {field('facebook', 'Facebook', { type: 'url', placeholder: 'https://' })}
                            {field('linkedin', 'LinkedIn', { type: 'url', placeholder: 'https://' })}
                        </div>

                        <div className="space-y-2">
                            <Label>Category</Label>
                            <TagCombobox
                                options={categories}
                                value={tags}
                                onChange={setTags}
                                placeholder="Select or create categories"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} className="gap-2">
                            {saving && <Loader2 size={16} className="animate-spin" />}
                            {customer ? 'Save Changes' : 'Add Customer'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CustomerFormDialog;
